import { StoreProduct } from "@medusajs/types";
import { useSearchParams } from "next/navigation";
import { useCheckoutStore } from "@/stores/checkout-store";
import { useCart } from "@/hooks/useCart";
import { formatPrice } from "@/lib/utils";
import { useMemo } from "react";

export function useProductVariant(
  medusaProduct: StoreProduct | undefined,
  medusaId: string,
  options?: { seedFromUrl?: boolean }
) {
  const searchParams = useSearchParams();

  // Use an explicit selector for the customization object so Zustand re-renders
  // this hook's consumer SPECIFICALLY when productCustomizations[medusaId]
  // changes — not just when any part of the store changes.
  const customization = useCheckoutStore((state) => state.productCustomizations?.[medusaId]);
  const setProductCustomization = useCheckoutStore((state) => state.setProductCustomization);

  const { cart } = useCart();

  const variants = useMemo(() => medusaProduct?.variants ?? [], [medusaProduct?.variants]);
  const variantIds = useMemo(() => variants.map((variant) => variant.id), [variants]);
  const hasMultipleVariants = variants.length > 1;

  // ── 1. Surface currently reflected in the cart for this product ──────────
  const surfaceFromCart = useMemo(() => {
    if (!hasMultipleVariants) return undefined;
    const cartItem = cart?.items?.find((item) => variantIds.includes(item.variant_id ?? ""));
    if (!cartItem) return undefined;
    return variants.find((v) => v.id === cartItem.variant_id)?.metadata?.["surface"] as number | undefined;
  }, [cart?.items, variantIds, hasMultipleVariants, variants]);

  // ── 2. Surface from URL — only read on PDP (seedFromUrl: true) ───────────
  const surfaceFromUrl = options?.seedFromUrl ? Number(searchParams.get("surface")) || undefined : undefined;

  // ── 3. Surface persisted in the store (keyed per product) ────────────────
  // customization is now read directly via selector above — no getter call needed.
  const surfaceFromStore = customization?.surface;

  // ── 4. Priority: URL (PDP first-visit seed) → store → cart → product min ─
  const resolvedSurface =
    surfaceFromUrl ??
    surfaceFromStore ??
    surfaceFromCart ??
    (medusaProduct?.metadata?.surface_min as number | undefined);

  // ── 5. Resolved variant object ────────────────────────────────────────────
  const selectedVariant = hasMultipleVariants
    ? (variants.find((v) => v.metadata?.["surface"] == resolvedSurface) ?? variants[0])
    : variants[0];

  const selectedVariantPrice = formatPrice(selectedVariant?.calculated_price?.calculated_amount ?? 0, {
    showDecimals: true,
  });

  // ── Setter: always writes to store; on PDP also syncs URL ─────────────────
  const setVariant = (surface: number) => {
    setProductCustomization(medusaId, { surface });
    if (options?.seedFromUrl && typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.set("surface", String(surface));
      window.history.replaceState(null, "", url);
    }
  };

  return {
    hasMultipleVariants,
    resolvedSurface,
    selectedVariant,
    selectedVariantPrice,
    setVariant,
    customization,
    hasCustomization: customization !== undefined,
    surfaceFromStore,
    surfaceFromCart,
    surfaceFromUrl,
  };
}
